/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IInspectorControl_ViewSelector, TInspectorControlProps } from "../InspectorControlTypes";
import {
	FieldGroup,
	FieldResourceSelector,
	FieldResourceSelectorPanel,
	FieldWrapper,
	Icon,
	InputAdornment,
	InputButton
} from "@adapptio/lib-editor-ui";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { EditorMainContext } from "../../../ui/contexts/EditorMainContext";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { IResourceNode, RESOURCE_LIST_STYLE, ResourceList } from "../../../ui/components/ResourceList";
import { useTranslate } from "@adapptio/lib-components";
import { termsEditorCommon } from "../../../terms/common";
import { useEscKey } from "../../../ui/hooks/useEscKey";
import { isNilOrEmptyString, offEvent, onEvent } from "@adapptio/lib-shared";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";

export interface IInspectorControlViewSelectorProps
	extends TInspectorControlProps<IInspectorControl_ViewSelector> {}

const AllowedResourceTypes_View = [ RESOURCE_TYPES.VIEW ];

export const InspectorControlViewSelector: React.FC<IInspectorControlViewSelectorProps> = ({
	model: _model
}) => {
	const editorCtx = useContext(EditorMainContext);
	const inspectorContext = useEditorInspectorContext();

	const { model, validationErrors } = useBlueprintModel(_model, "constString");

	const createCommandId = editorCtx.resourceService.getResourceCreateCommandId(RESOURCE_TYPES.VIEW);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	const handleCreate = useCallback(() => {
		if (!createCommandId) {
			return;
		}

		closeResourceList();

		editorCtx.commandsService
			.execute(createCommandId)
			.then((resourceId: string) => {
				if (resourceId) {
					model.schema.setValue(model, resourceId, true);
				}
			})
			.catch((err) => {
				console.warn("Failed to create new view:", err);
			});
	}, [ editorCtx, model ]);

	const handleOpen = useCallback(() => {
		editorCtx.commandsService.execute("resource.open", model.value).catch((err) => {
			console.warn("Failed to open action resource:", err);
		});
	}, [ editorCtx, model ]);

	const handleClear = useCallback(() => {
		model.schema.setValue(model, null, true);
	}, [ editorCtx, model ]);

	const value = model.value;
	const placeholder = undefined;
	const onChange = handleChange;
	const listAnchor = undefined;
	const listDirection = undefined;
	const listOffset = undefined;
	const allowedResourceTypes = AllowedResourceTypes_View;
	const readOnly = inspectorContext?.readOnly;

	// @TODO: Move this to adpt-editor-ui FieldResource
	const t = useTranslate();

	const [ isListOpened, setIsListOpened ] = useState(false);
	const selectorRef = useRef<HTMLButtonElement>();

	const [ label, setLabel ] = useState(() => {
		return value
			? editorCtx.resourceService.getResourceDescriptorById(value)?.name ||
					t("editor", termsEditorCommon.fields.resource.notFound)
			: t("editor", placeholder) || t("editor", termsEditorCommon.fields.resource.select);
	});

	const toggleResourceList = useCallback(() => {
		setIsListOpened((x) => !x);
	}, []);

	const closeResourceList = useCallback(() => {
		setIsListOpened(false);
	}, []);

	const handleSelect = useCallback(
		(resourceNode: IResourceNode) => {
			onChange(resourceNode.id);
			setIsListOpened(false);
		},
		[ onChange ]
	);

	useEscKey(() => setIsListOpened(false), isListOpened);

	useEffect(() => {
		if (value) {
			const resource = editorCtx.resourceService.getResourceById(value);

			if (resource) {
				const handleChange = () => setLabel(resource.descriptor.name);
				const handleDelete = () => setLabel(t("editor", termsEditorCommon.fields.resource.deleted));

				handleChange();

				onEvent(resource.onChange, handleChange);
				onEvent(resource.onDelete, handleDelete);

				return () => {
					offEvent(resource.onChange, handleChange);
					offEvent(resource.onDelete, handleDelete);
				};
			} else {
				setLabel(t("editor", termsEditorCommon.fields.resource.notFound));
			}
		} else {
			setLabel(t("editor", placeholder) || t("editor", termsEditorCommon.fields.resource.select));
		}
	}, [ editorCtx.resourceService, value ]);

	const resourceSelected = !isNilOrEmptyString(value);
	const showClear = !model.schema.opts.constraints?.required && resourceSelected;
	const showOpen = resourceSelected;
	const showPlus = !resourceSelected && !!createCommandId;

	return (
		<FieldGroup grow>
			<FieldWrapper type="resource" grow isFocused>
				<FieldResourceSelector
					ref={selectorRef}
					onClick={toggleResourceList}
					disabled={readOnly}
					tooltip={label}
					errors={validationErrors}
					endAdornment={
						<InputAdornment>
							{showClear && (
								<InputButton
									type="button"
									title="Clear"
									onClick={handleClear}
									disabled={!resourceSelected}
								>
									<Icon name="mdi/close" />
								</InputButton>
							)}
							{showOpen && (
								<InputButton
									type="button"
									disabled={!resourceSelected}
									onClick={handleOpen}
									title="Open view"
								>
									<Icon name="mdi/open-in-new" />
								</InputButton>
							)}
							{showPlus && (
								<InputButton type="button" onClick={handleCreate} title="Create new view">
									<Icon name="mdi/plus" />
								</InputButton>
							)}
						</InputAdornment>
					}
				>
					{label}
				</FieldResourceSelector>

				<FieldResourceSelectorPanel
					anchorEl={selectorRef}
					isOpened={isListOpened}
					panelAnchor={listAnchor}
					panelDirection={listDirection}
					panelOffset={listOffset}
					onClose={closeResourceList}
					title="Select view"
					onCreate={handleCreate}
				>
					<ResourceList
						onSelect={handleSelect}
						style={RESOURCE_LIST_STYLE.COMPACT}
						allowedResourceTypes={allowedResourceTypes}
						isActive={true}
					/>
				</FieldResourceSelectorPanel>
			</FieldWrapper>
		</FieldGroup>
	);
};
